import * as React from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { cn } from "@/lib/utils";
import { DashboardController } from "../../../controllers";
import ERoute from "../../../ERoute";
import RouteHelper from "../../../helpers/RouteHelper";
import {
  DashboardProjectBudget,
  DashboardProjectBudgetHealth,
  DashboardProjectBudgetHealthStatus,
  ProjectBudgetType,
} from "../../../types";
import NumberFormatter from "../../../utilities/NumberFormatter";
import CardEmptyInfo from "../../Card/CardEmptyInfo";
import PageLoader from "../../Page/PageLoader";
import Panel from "../../Panel/Panel";
import { buttonVariants } from "../../ui/button";
import { Progress } from "../../ui/progress";

interface IProps {
  currency: string;
  numberFormat: string;
}

const HEALTH_TEXT_CLASSES: Record<DashboardProjectBudgetHealthStatus, string> = {
  healthy: "text-muted-foreground",
  near_budget: "text-amber-600 dark:text-amber-500",
  over_budget: "text-destructive",
};

// The indicator is rendered inside Progress, so color it through its data-slot
const HEALTH_INDICATOR_CLASSES: Record<DashboardProjectBudgetHealthStatus, string> = {
  healthy: "",
  near_budget: "[&_[data-slot=progress-indicator]]:bg-amber-500",
  over_budget: "[&_[data-slot=progress-indicator]]:bg-destructive",
};

const ProjectBudgetHealthWidget = (props: IProps) => {
  const { numberFormat } = props;
  const { t } = useTranslation();
  const [isLoading, setIsLoading] = React.useState(true);
  const [data, setData] = React.useState<DashboardProjectBudgetHealth | null>(null);

  React.useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setData(await DashboardController.getProjectBudgetHealth());
    } catch (error) {
      console.error("Failed to fetch project budget health:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const formatBudgetValue = (project: DashboardProjectBudget, value: number) => {
    if (project.budget_type === ProjectBudgetType.TOTAL_HOURS) {
      return t("ProjectBudgetHealthWidget::{{hours}} hrs", { hours: NumberFormatter.formatNumber(numberFormat, value) });
    }

    return NumberFormatter.formatCurrency(data.currency, numberFormat, value);
  };

  const renderRemaining = (project: DashboardProjectBudget) => {
    const remaining = Number(project.remaining);

    if (remaining < 0) {
      return t("ProjectBudgetHealthWidget::{{value}} over", { value: formatBudgetValue(project, Math.abs(remaining)) });
    }

    return t("ProjectBudgetHealthWidget::{{value}} left", { value: formatBudgetValue(project, remaining) });
  };

  const title = t("ProjectBudgetHealthWidget::Project budget health");
  const viewAllLink = (
    <Link to={ERoute.PATH_PROJECTS_ACTIVE} className={buttonVariants({ variant: "link", size: "sm" })}>
      {t("ProjectBudgetHealthWidget::Go to projects")}
    </Link>
  );

  if (isLoading || !data) {
    return (
      <Panel className="h-full flex flex-col mb-0!" contentClasses="min-h-0 flex-1 overflow-auto" title={title}>
        {isLoading ? <PageLoader /> : null}
      </Panel>
    );
  }

  return (
    <Panel
      className="h-full flex flex-col mb-0!"
      contentClasses="min-h-0 flex-1 flex flex-col p-0!"
      title={title}
      headerRight={viewAllLink}
    >
      {data.count === 0 ? (
        <div className="flex flex-1 items-center justify-center">
          <CardEmptyInfo icon="project" description={t("ProjectBudgetHealthWidget::No active projects with a budget")} />
        </div>
      ) : (
        <>
          <div className="flex shrink-0 gap-8 border-b px-4 py-3">
            <div>
              <div className="text-xs text-muted-foreground">{t("ProjectBudgetHealthWidget::Over budget")}</div>
              <div className={cn("text-xl font-bold tabular-nums", data.over_budget_count > 0 && "text-destructive")}>
                {data.over_budget_count}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">{t("ProjectBudgetHealthWidget::Close to budget")}</div>
              <div
                className={cn(
                  "text-xl font-bold tabular-nums",
                  data.near_budget_count > 0 && HEALTH_TEXT_CLASSES.near_budget
                )}
              >
                {data.near_budget_count}
              </div>
            </div>
            <div>
              <div className="text-xs text-muted-foreground">{t("ProjectBudgetHealthWidget::Projects")}</div>
              <div className="text-xl font-bold tabular-nums">{data.count}</div>
            </div>
          </div>

          {/* On phones widgets take their natural height, so cap the list there and let it scroll */}
          <ul className="m-0 min-h-0 flex-1 list-none overflow-auto p-0 in-[.dashboard-mobile]:max-h-80">
            {data.projects.map((project) => {
              const usedPercentage = Math.round(Number(project.used_ratio) * 100);

              return (
                <li key={project.id} className="border-b last:border-b-0">
                  <Link
                    to={RouteHelper.process(ERoute.PATH_PROJECT, { id: project.id })}
                    className="block px-4 py-2.5 text-foreground no-underline hover:text-foreground"
                  >
                    <div className="flex items-baseline justify-between gap-2">
                      <span className="truncate font-medium">{project.name}</span>
                      <span
                        className={cn("shrink-0 font-semibold tabular-nums", HEALTH_TEXT_CLASSES[project.health])}
                      >
                        {usedPercentage}%
                      </span>
                    </div>
                    <Progress
                      value={Math.min(usedPercentage, 100)}
                      className={cn("my-1.5", HEALTH_INDICATOR_CLASSES[project.health])}
                      aria-label={project.name}
                    />
                    <div className="flex flex-wrap gap-x-1.5 text-xs text-muted-foreground">
                      {project.contact_name && (
                        <>
                          <span className="truncate">{project.contact_name}</span>
                          <span>·</span>
                        </>
                      )}
                      <span className="tabular-nums">
                        {formatBudgetValue(project, Number(project.used))} /{" "}
                        {formatBudgetValue(project, Number(project.budget))}
                        {project.resets_monthly && ` ${t("ProjectBudgetHealthWidget::this month")}`}
                      </span>
                      <span>·</span>
                      <span className={HEALTH_TEXT_CLASSES[project.health]}>{renderRemaining(project)}</span>
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </Panel>
  );
};

export default ProjectBudgetHealthWidget;
